import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type Counterparty, type ViesCheck } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date as formatDate } from '../format';

/**
 * Stan numeru VAT-UE kontrahenta przy wystawianiu faktury (E8-S4).
 *
 * To jest moment, w którym decyzja naprawdę zapada: stawka 0% przy dostawie do Unii i brak
 * polskiego VAT-u przy usłudze rozliczanej przez nabywcę zależą od tego, czy numer jest ważny
 * (art. 42 ust. 1 pkt 1 ustawy o VAT). Dlatego pokazujemy tu ostatnie sprawdzenie i pozwalamy
 * zapytać VIES bez wychodzenia z formularza.
 *
 * **Niczego nie blokujemy.** Wystawienie faktury zostaje decyzją człowieka: może mieć inne dowody,
 * a rejestr państwa członkowskiego bywa chwilowo niedostępny. Ostrzegamy tylko wtedy, gdy stawka
 * naprawdę tego dotyczy — przy sprzedaży ze stawką krajową numer VAT-UE nie zmienia niczego i
 * migający pasek byłby hałasem.
 */
export function ViesStatus({
  companyId,
  counterparty,
  matters,
  t,
  locale,
}: {
  companyId: string;
  counterparty: Counterparty | undefined;
  /** Czy stawki na fakturze zależą od numeru: dostawa 0% albo usługa „np" dla kontrahenta z UE. */
  matters: boolean;
  t: Dictionary;
  locale: Locale;
}) {
  const [check, setCheck] = useState<ViesCheck | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const counterpartyId = counterparty?.id;
  const eu = counterparty?.type === 'EU';

  const load = useCallback(async () => {
    if (!counterpartyId || !eu) return;
    setNote(null);
    try {
      const history = await api.viesHistory(companyId, counterpartyId);
      setCheck(history[0] ?? null);
    } catch {
      setCheck(null);
    } finally {
      setLoaded(true);
    }
  }, [companyId, counterpartyId, eu]);

  useEffect(() => {
    setCheck(null);
    setLoaded(false);
    void load();
  }, [load]);

  if (!eu || !counterpartyId) return null;

  const unchecked = loaded && check === null;
  const warn = matters && (unchecked || (check !== null && !check.valid));

  return (
    <div className={warn ? 'alert a-warn' : 'row'}>
      <div>
        <b>{t.invoices.viesTitle}</b>{' '}
        {check === null ? (
          <span className="sm muted">{t.vatUe.viesUnknown}</span>
        ) : (
          <span className={check.valid ? 'chip st-ok' : 'chip st-warn'}>
            <i />
            {check.valid ? t.vatUe.viesValid : t.vatUe.viesInvalid} ·{' '}
            {formatDate(check.checkedAt.slice(0, 10), locale)}
          </span>
        )}{' '}
        <button
          type="button"
          className="btn ghost sm"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setNote(null);
            api
              .checkVies(companyId, counterpartyId)
              .then((wynik) => setCheck(wynik))
              // Milczenie rejestru to nie „numer nieważny" — mówimy dokładnie to, co przyszło
              .catch((failure) => setNote(failure instanceof ApiError ? failure.message : String(failure)))
              .finally(() => setBusy(false));
          }}
        >
          {t.invoices.viesCheck}
        </button>
        {warn && <p className="sm">{t.invoices.viesWarning}</p>}
        {note && <p className="sm">{note}</p>}
      </div>
    </div>
  );
}
